import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useStore } from '../../store/store'
import ContainerWrapper from '../Container/ContainerWrapper'
import { navbarHeight, pageHeight, pageWidth, sidebarWidth } from '../../constants/gridConfig'
import { VList } from 'virtua'
import { Button, Select, Switch, Tree } from 'antd'
import { ItemTypes, MASKS_TYPE, viewerTypes } from '../../constants/constants'
import Container from '../Container/Container'
import { PreviewProvider } from '../../contexts/PreviewContext'
import { maxContainerHeight } from '../../utils/containers'
import { displayTitleIndex, getTitlesConfig, numberedTitleLevels, titleDisplayIndex } from '../../utils/titles'
import { generateTreeFromFlatBoxes } from '../LeftSide/SummarySide'
import { useContainersToShow, useWindowDimensions } from '../../hooks'
import { DownOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons'
import { isWatermarkOnPage } from '../../utils/watermark'
import { PAGE_BACKGROUND_Z_INDEX, containerPageBackgroundId, findPageBackgroundById } from '../../utils/pageBackground'
import WatermarkOverlay from './WatermarkOverlay'

const SUMMARY_WIDTH = 400
const SUMMARY_MIN_WIDTH = 220

const Item = React.memo(({ index, forceHover }) => {
  const header = useStore(({ masks }) => masks.find(({ id }) => id === MASKS_TYPE.HEADER.id))
  const footer = useStore(({ masks }) => masks.find(({ id }) => id === MASKS_TYPE.FOOTER.id))
  const masks = useStore(({ masks }) => masks)
  const pagesPreview = useStore(({ pagesPreview }) => pagesPreview)
  const configuration = useStore(({ configuration }) => configuration)
  const containers = useStore(({ containers }) => containers)
  const landscape = useStore(({ landscape }) => landscape)
  const formIncomplete = useStore(({ previewFormIncomplete }) => previewFormIncomplete)
  const pageInfos = pagesPreview[index]
  const isFullHeight = pagesPreview[index].isFullHeight
  const printedContainers = pageInfos.printedContainers
  const ref = useRef()

  // Décor de la page : celui de son premier container — le modèle, ou la section
  // à décor propre d'où il vient. Une page ne mélange jamais deux décors, le
  // changement de décor étant un saut de page garanti (cf. buildSegments côté
  // service). Vide : rien à dessiner, c'est ce qu'attend une section qui efface
  // le décor du document.
  const background = useMemo(() => {
    const first = containers.find((c) => c.id === printedContainers[0]?.id)
    const id = containerPageBackgroundId(first)
    const mask = findPageBackgroundById(masks, id)
    return (mask?.boxes?.length ?? 0) > 0 ? mask : null
  }, [containers, masks, printedContainers])

  const renderContainer = (containerInfos, idx) => {
    const isFirst = idx === 0
    const rendered = <PreviewProvider
      startFrom={containerInfos.startFrom}
      duppedBreakIdx={containerInfos.duppedBreakIdx}
      forceHover={forceHover}
      key={`${containerInfos.id}-${containerInfos.duppedBreakIdx}-${containerInfos.startFrom}`}
    >
      <ContainerWrapper
        containerId={containerInfos.id}
        fromViewer={viewerTypes.PREVIEW}
        hideAdder={isFirst}
        type='container' />
    </PreviewProvider>

    // Container plus haut qu'une page : le PDF n'en montre qu'une bande par
    // page (cf. computeSliceBoundaries côté service). Même clip ici, sinon
    // l'aperçu afficherait le container entier sur chacune de ses pages.
    if (typeof containerInfos.sliceHeight !== 'number') return rendered
    return <div
      key={`${containerInfos.id}-slice-${containerInfos.sliceTop}`}
      style={{ height: containerInfos.sliceHeight, overflow: 'hidden' }}
    >
      <div style={{ marginTop: -containerInfos.sliceTop }}>{rendered}</div>
    </div>
  }

  return <div
    ref={ref}
    style={{
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: "white",
      // Le décor et le filigrane passent sous le contenu (z-index négatif) mais
      // au-dessus de ce fond blanc.
      isolation: 'isolate',
    }}>
    {!isFullHeight && <Container id={header.id} key={header.id} fromViewer={viewerTypes.PREVIEW} />}
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: isFullHeight ? pageHeight : maxContainerHeight({ header, footer }),
      gap: configuration.lineSpacing,
    }}>
      {printedContainers.map((containerInfos, idx) => renderContainer(containerInfos, idx))}
    </div>
    {!isFullHeight && <Container id={footer.id} key={footer.id} fromViewer={viewerTypes.PREVIEW} />}
    {/* Décor de page, sur toute la page, sous le contenu, l'en-tête et le pied
        (cf. pdfApp). */}
    {!isFullHeight && background && <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      zIndex: PAGE_BACKGROUND_Z_INDEX,
      pointerEvents: 'none',
    }}>
      <Container id={background.id} key={background.id} fromViewer={viewerTypes.PREVIEW} />
    </div>}
    {/* Filigrane après le décor, donc au-dessus, mais sous le contenu ; sur
        une page pleine page, par-dessus le contenu, que son fond plein
        recouvrirait (cf. pageUnderlay du service). */}
    {isWatermarkOnPage(configuration.watermark, { isFullHeight, formIncomplete }) && <WatermarkOverlay
      watermark={configuration.watermark}
      width={landscape ? pageHeight : pageWidth}
      height={landscape ? pageWidth : pageHeight}
      onTop={isFullHeight}
    />}
  </div>
})

const PreviewOverlay = () => {
  const previewRenderState = useStore(({ previewRenderState }) => previewRenderState)
  const pagesPreview = useStore(({ pagesPreview }) => pagesPreview)
  const landscape = useStore(({ landscape }) => landscape)
  const configuration = useStore(({ configuration }) => configuration)
  const columns = useStore(({ columns }) => columns)
  const summaryBoxes = useStore(({ getSummaryBoxes }) => getSummaryBoxes())
  const boxes = useStore(({ boxes }) => boxes)
  const visibleContainers = useContainersToShow()
  const previewAnchorContainerId = useStore(({ previewAnchorContainerId }) => previewAnchorContainerId)
  const saveAndGeneratePdf = useStore(({ saveAndGeneratePdf }) => saveAndGeneratePdf)
  const isPreviewLoading = useStore(({ isPreviewLoading }) => isPreviewLoading)
  const [forceHover, setForceHover] = useState(false)
  const listRef = useRef();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions()

  // Les boutons « Retour au document » / « Générer le PDF » flottent (portail)
  // à l'emplacement de l'onglet Export. On mesure leur bord droit : la page et
  // le sommaire se rangent à sa droite.
  const [floatingButtonsRight, setFloatingButtonsRight] = useState(0)
  useLayoutEffect(() => {
    const buttons = document.querySelectorAll('#PreviewPortal button')
    setFloatingButtonsRight(Math.max(0, ...Array.from(buttons, (button) => button.getBoundingClientRect().right)))
  }, [previewRenderState, pagesPreview, windowWidth, windowHeight])

  const titleLevel1Options = useMemo(() => {
    if (!visibleContainers?.length || !boxes?.length) return [];
    const titleBoxesByColumnId = new Map();
    boxes.forEach((box) => {
      if (box.type === ItemTypes.TITLE) {
        titleBoxesByColumnId.set(box.columnId, box);
      }
    });
    if (titleBoxesByColumnId.size === 0) return [];

    return visibleContainers
      .map((container, index) => {
        let titleBox = null;
        for (const columnId of container.columnsIds) {
          const candidate = titleBoxesByColumnId.get(columnId);
          if (candidate) {
            titleBox = candidate;
            break;
          }
        }
        if (!titleBox) return null;

        const titleText = titleBox?.content?.editorState
          ?.getCurrentContent?.()
          ?.getPlainText?.() || '';
        const titleIndex = displayTitleIndex(
          titleDisplayIndex(titleBox?.content),
          configuration.separatorTitle,
          getTitlesConfig(configuration),
          numberedTitleLevels(configuration),
          configuration.finalSeparatorTitle
        );
        const label = `${titleIndex ? `${titleIndex} ` : ''}${titleText || 'Sans titre'}`;

        return {
          containerId: container.id,
          label,
          index,
          rootIndex: titleBox?.content?.indexTitle?.[0],
        };
      })
      .filter(Boolean);
  }, [boxes, visibleContainers, configuration]);

  const containerIdByColumnId = useMemo(() => {
    const map = new Map();
    visibleContainers.forEach((container) => {
      container.columnsIds.forEach((columnId) => {
        map.set(columnId, container.id);
      });
    });
    return map;
  }, [visibleContainers]);

  const pageIndexByContainerId = useMemo(() => {
    const map = new Map();
    pagesPreview.forEach((page, pageIndex) => {
      page.printedContainers.forEach((printed) => {
        if (printed?.id && !map.has(printed.id)) {
          map.set(printed.id, pageIndex);
        }
      });
    });
    return map;
  }, [pagesPreview]);

  const handleSummarySelect = useCallback(
    (_, info) => {
      const columnId = info?.node?.columnId;
      if (!columnId) return;
      const containerId = containerIdByColumnId.get(columnId);
      if (!containerId) return;
      const pageIndex = pageIndexByContainerId.get(containerId);
      if (typeof pageIndex === 'number') {
        listRef.current?.scrollToIndex?.(pageIndex);
      }
    },
    [containerIdByColumnId, pageIndexByContainerId]
  );

  const currentTitleContainerId = useMemo(() => {
    if (!titleLevel1Options.length) return null;
    if (!previewAnchorContainerId) return titleLevel1Options[0].containerId;

    const visibleIndex = visibleContainers.findIndex(
      (container) => container.id === previewAnchorContainerId
    );
    if (visibleIndex < 0) return titleLevel1Options[0].containerId;

    let selected = null;
    for (const option of titleLevel1Options) {
      if (option.index <= visibleIndex) {
        selected = option.containerId;
      } else {
        break;
      }
    }
    return selected || titleLevel1Options[0].containerId;
  }, [previewAnchorContainerId, visibleContainers, titleLevel1Options]);

  const selectedRootIndex = useMemo(() => {
    if (!titleLevel1Options.length) return null;
    const option = titleLevel1Options.find((opt) => opt.containerId === currentTitleContainerId);
    return option?.rootIndex ?? null;
  }, [currentTitleContainerId, titleLevel1Options]);

  const summaryTreeData = useMemo(() => {
    if (!selectedRootIndex) return [];
    const boxesForRoot = summaryBoxes.filter(
      (box) => box.indexTitle?.[0] === selectedRootIndex
    );
    return generateTreeFromFlatBoxes({
      boxes: boxesForRoot,
      separator: configuration.separatorTitle,
      titlesTypeConfig: getTitlesConfig(configuration),
      numberedLevels: numberedTitleLevels(configuration),
      finalSeparator: configuration.finalSeparatorTitle,
      columns,
    });
  }, [columns, configuration, selectedRootIndex, summaryBoxes]);

  // Largeur gardée à gauche de la page : les boutons flottants, puis le sommaire
  // à sa largeur minimale. Une marge fixe de 200px ne suffisait pas quand la
  // largeur de la fenêtre bornait le zoom : le sommaire passait sous les boutons.
  const reservedLeft = Math.max(
    200,
    floatingButtonsRight + 16 + (summaryTreeData.length > 0 ? SUMMARY_MIN_WIDTH : 0)
  )
  const zoom1 = (windowWidth - sidebarWidth - 80 - reservedLeft) / (landscape ? pageHeight : pageWidth);
  const zoom2 = (windowHeight - navbarHeight) / (pageHeight + 50);
  const zoom = Math.min(zoom1, zoom2);
  const summaryRight = sidebarWidth + (landscape ? pageHeight : pageWidth) * zoom + 80
  const summaryWidth = Math.max(
    SUMMARY_MIN_WIDTH,
    Math.min(SUMMARY_WIDTH, windowWidth - summaryRight - floatingButtonsRight - 16)
  )

  const [summaryExpandedKeys, setSummaryExpandedKeys] = useState([]);
  const prevPreviewLoadingRef = useRef(isPreviewLoading);

  useEffect(() => {
    const prev = prevPreviewLoadingRef.current;
    if (prev && !isPreviewLoading && summaryTreeData.length) {
      setSummaryExpandedKeys([summaryTreeData[0].key]);
    }
    prevPreviewLoadingRef.current = isPreviewLoading;
  }, [isPreviewLoading, summaryTreeData]);

  const handleTitleSelect = useCallback(
    (containerId) => {
      listRef.current?.scrollToIndex?.(0);
      saveAndGeneratePdf('preview', null, containerId);
    },
    [saveAndGeneratePdf]
  );

  const selectedTitleIndex = useMemo(() => {
    if (!currentTitleContainerId) return -1;
    return titleLevel1Options.findIndex((option) => option.containerId === currentTitleContainerId);
  }, [currentTitleContainerId, titleLevel1Options]);

  const goToPrevTitle = useCallback(() => {
    if (selectedTitleIndex <= 0) return;
    const prev = titleLevel1Options[selectedTitleIndex - 1];
    if (prev) handleTitleSelect(prev.containerId);
  }, [handleTitleSelect, selectedTitleIndex, titleLevel1Options]);

  const goToNextTitle = useCallback(() => {
    if (selectedTitleIndex < 0 || selectedTitleIndex >= titleLevel1Options.length - 1) return;
    const next = titleLevel1Options[selectedTitleIndex + 1];
    if (next) handleTitleSelect(next.containerId);
  }, [handleTitleSelect, selectedTitleIndex, titleLevel1Options]);

  const renderPage = (index) => {
    return <div
      style={{
        paddingBottom: 20,
      }}>
      <div style={{
        width: landscape ? pageHeight : pageWidth,
        zoom: `${zoom}`,
      }}
      >
        <Item index={index} forceHover={forceHover} />
      </div>
    </div>
  }
  // Cible des boutons flottants d'ExportPanel (portail). Même `key` dans les deux
  // retours : React réutilise le même noeud DOM quand l'aperçu s'ouvre. Sans
  // cela, ExportPanel (qui résout `#PreviewPortal` pendant son rendu) montait
  // ses boutons dans l'ancienne racine : elle perdait son id, la mesure
  // ci-dessus ne trouvait aucun bouton, et ils se retrouvaient avant le
  // sommaire dans le DOM, donc dessous.
  const previewPortal = <div key='PreviewPortal' id='PreviewPortal' />
  if (!previewRenderState || !pagesPreview || pagesPreview.length === 0) return <div>
    {previewPortal}
  </div>

  return (
    <div style={{
      width: '100%',
      height: '100%',
      backdropFilter: 'blur(6px)',
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      position: 'fixed',
      top: 0,
      bottom: 0,
      zIndex: 1000
    }}>
      {summaryTreeData.length > 0 && (
        <div style={{
          position: 'fixed',
          height: "100%",
          right: summaryRight,
          top: navbarHeight,
          backgroundColor: "#6d6d7c",
          borderRight: "1px solid black",
          borderTopLeftRadius: 8,
          borderBottomLeftRadius: 8,
          width: summaryWidth,
        }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              paddingTop: 20,
            }}>
            <div style={{ display: "flex", padding: "0 10px", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ color: "white", fontSize: 12, fontWeight: 500 }}>
                Afficher espaces / sauts
              </div>
              <Switch
                checked={forceHover}
                onChange={setForceHover}
              />
            </div>
            <div style={{ display: "flex", padding: "0 10px" }}>
              <Select
                value={currentTitleContainerId || ''}
                onChange={handleTitleSelect}
                style={{ width: "100%" }}
                options={titleLevel1Options.map((option) => ({
                  value: option.containerId,
                  label: option.label,
                }))}
              />
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
              <Button
                type="default"
                icon={<LeftOutlined />}
                onClick={goToPrevTitle}
                disabled={selectedTitleIndex <= 0}
                style={{ borderRadius: 6 }}
              />
              <Button
                type="default"
                icon={<RightOutlined />}
                onClick={goToNextTitle}
                disabled={selectedTitleIndex < 0 || selectedTitleIndex >= titleLevel1Options.length - 1}
                style={{ borderRadius: 6 }}
              />
            </div>
            <Tree
              style={{
                backgroundColor: "#6d6d7c",
                "--custom-indent-size": "30px"
              }}
              height={windowHeight - navbarHeight - 80}
              expandedKeys={summaryExpandedKeys}
              onExpand={(keys) => setSummaryExpandedKeys(keys)}
              showLine
              switcherIcon={<DownOutlined style={{ color: "white" }} />}
              onSelect={handleSummarySelect}

              titleRender={(node) => (
                <div style={{
                  maxWidth: Math.min(210, summaryWidth - 90),
                  overflowWrap: "anywhere",
                  color: 'white',
                  fontStyle: node.isMissingParent ? "italic" : "normal",
                  opacity: node.isMissingParent ? 0.7 : 1
                }}>
                  {node.title || "(vide)"}
                </div>
              )}
              treeData={summaryTreeData}
            />
          </div>
        </div>
      )}
      <div style={{
        position: 'fixed',
        height: "100%",
        right: sidebarWidth,
        top: navbarHeight,
      }}>
        <VList
          ref={listRef}
          style={{
            width: (landscape ? pageHeight : pageWidth) * zoom + 80,
            height: windowHeight - navbarHeight,
            color: configuration.baseFontColor?.style,
            borderTopRightRadius: 8,
            borderBottomRightRadius: 8,
            backgroundColor: "#323232",
            paddingTop: 20,
          }}
          className='no-scrollbar'
        >
          {pagesPreview.map((_, index) => (
            <div key={index} style={{ display: 'flex', justifyContent: 'center' }}>
              {renderPage(index)}
            </div>
          ))}
        </VList>
      </div>
      {previewPortal}
    </div>
  )
}

export default PreviewOverlay
